import type { PaypointFormData } from "Components/Paypoints/schema";
import { FormSection } from "Components/Shared/FormSection";
import { BankAccountDropdown } from "Components/Shared/Inputs/Form/BankAccountDropdown";
import { EmployeeAutocomplete } from "Components/Shared/Inputs/Form/EmployeeAutocomplete";
import type { BankAccountListItem, EmployeeDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import type { Control } from "react-hook-form";

const RESOURCES = Resources.Paypoint.Form;

export type AdvancedFieldsVariant = "editable" | "readOnly" | "notAvailable";

type Props = {
  control: Control<PaypointFormData>;
  accounts: Array<BankAccountListItem>;
  employees: Array<EmployeeDto>;
  bankAccountVariant: AdvancedFieldsVariant;
  employeesVariant: AdvancedFieldsVariant;
  isBalanceVisible?: boolean;
  isBankAccountLocked: boolean;
};

// Who receives the money and who may operate the paypoint.
export const PaypointAccessSection = ({
  control,
  accounts,
  employees,
  bankAccountVariant,
  employeesVariant,
  isBalanceVisible,
  isBankAccountLocked,
}: Props) => {
  const { t } = useResource();

  return (
    <FormSection legend={t(RESOURCES.Legend.PaypointSettings)}>
      <BankAccountDropdown
        name="bankAccountPublicID"
        control={control}
        label={t(RESOURCES.PaypointAccount)}
        accounts={accounts}
        disabled={bankAccountVariant !== "editable" || isBankAccountLocked}
        isBalanceVisible={isBalanceVisible}
        emptyValueText={
          bankAccountVariant === "notAvailable"
            ? t(Resources.Common.NotAvailable)
            : undefined
        }
        helperText={
          bankAccountVariant === "notAvailable"
            ? t(RESOURCES.BankAccountNotAccessible)
            : " " // to create same spacing as other fields
        }
      />
      <EmployeeAutocomplete
        employees={employees}
        control={control}
        name="employeePublicIDs"
        label={t(RESOURCES.Employees)}
        disabled={employeesVariant !== "editable"}
        emptyValueText={
          employeesVariant === "notAvailable"
            ? t(Resources.Common.NotAvailable)
            : undefined
        }
        helperText={
          employeesVariant === "notAvailable"
            ? t(RESOURCES.EmployeesNotAccessible)
            : undefined
        }
      />
    </FormSection>
  );
};
